/**
 * HomeCover — the entire first viewport is one edge-to-edge photographic
 * composition. The navigation lives inside it as an editorial table of
 * contents: four large chapter rows that preview their section by gently
 * cross-fading the background photograph on hover or keyboard focus.
 *
 * Headline copy never changes on hover; only the image and its caption do.
 * All photographs are existing, retouched project assets.
 */

import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CHAPTERS, COVER_IMAGE } from "@/components/nav/chapters";
import { useAnchorNav } from "@/utils/anchorNav";

interface Entry {
  label: string;
  context: string;
  caption: string;
}

const HomeCover = () => {
  const { t } = useTranslation();
  const handleAnchor = useAnchorNav();
  const [active, setActive] = useState<number | null>(null);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    let inner = 0;
    const raf = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(inner);
    };
  }, []);

  const entries = t("home.cover.entries", { returnObjects: true }) as Entry[];
  const sectors = t("home.hero.sectors", { returnObjects: true }) as string[];
  const defaultCaption = t("home.cover.defaultCaption");

  const layers = useMemo(
    () => [{ src: COVER_IMAGE, alt: defaultCaption }, ...CHAPTERS.map((c, i) => ({ src: c.image, alt: entries[i]?.caption ?? "" }))],
    [defaultCaption, entries],
  );

  const clear = useCallback(() => setActive(null), []);

  return (
    <section
      className={`relative isolate overflow-hidden bg-ink text-white ${entered ? "stagger-in" : ""}`}
      aria-labelledby="cover-heading"
    >
      {/* Photographic layers — stacked, cross-faded, stable dimensions */}
      <div className="absolute inset-0" aria-hidden="true">
        {layers.map((layer, i) => (
          <img
            key={`layer-${i}`}
            src={layer.src}
            alt=""
            className="cover-layer absolute inset-0 h-full w-full object-cover object-[58%_center]"
            style={{ opacity: (active === null ? 0 : active + 1) === i ? 1 : 0 }}
            loading={i === 0 ? "eager" : "lazy"}
            decoding="async"
            {...{ fetchpriority: i === 0 ? "high" : "low" }}
          />
        ))}
      </div>
      <div
        className="absolute inset-0 bg-[linear-gradient(105deg,hsl(var(--ink-deep)/0.95)_0%,hsl(var(--ink)/0.82)_38%,hsl(var(--ink)/0.55)_66%,hsl(var(--ink-deep)/0.78)_100%)]"
        aria-hidden="true"
      />

      <div className="relative z-10 container mx-auto px-6">
        <div className="grid min-h-[100svh] grid-cols-1 content-end gap-10 pb-10 pt-28 md:pb-14 md:pt-32 lg:grid-cols-12 lg:items-end lg:gap-12 lg:pb-16">
          {/* Statement */}
          <div className="lg:col-span-6 xl:col-span-6">
            <span className="mb-6 block h-px w-16 bg-cobalt-bright md:mb-8" aria-hidden="true" />


            <h1 id="cover-heading" className="display-xl text-white">
              {t("home.hero.display")}
            </h1>

            <ul className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-semibold uppercase tracking-[0.14em] text-white/85 md:mt-7 md:text-sm">
              {sectors.map((s) => (
                <li key={s} className="border border-white/25 px-3 py-1.5">
                  {s}
                </li>
              ))}
            </ul>

            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
              {t("home.hero.subheading")}
            </p>

            <p
              className="mt-7 hidden max-w-md border-l border-white/25 pl-4 text-xs uppercase tracking-[0.14em] text-white/60 transition-opacity duration-300 lg:block"
              aria-live="polite"
            >
              {active === null ? defaultCaption : entries[active]?.caption}
            </p>
          </div>

          {/* Table of contents */}
          <nav
            className="relative lg:col-span-6 lg:col-start-7 xl:col-span-5 xl:col-start-8 lg:bg-ink-deep/45 lg:p-6 lg:backdrop-blur-[3px]"
            aria-label={t("nav.contents")}
          >
            <p className="eyebrow mb-3 text-white/55 md:mb-4">{t("nav.contents")}</p>
            <ul className="border-t border-white/20">
              {CHAPTERS.map((c, i) => {
                const entry = entries[i];
                const rowClass =
                  "group flex w-full items-start gap-4 border-b border-white/20 py-4 text-left transition-colors duration-300 hover:bg-white/[0.07] focus-visible:bg-white/[0.07] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white md:gap-5 md:py-5";
                const inner = (
                  <>
                    <span className="index-num mt-1 text-sm text-cobalt-bright transition-transform duration-300 group-hover:-translate-y-0.5 group-focus-visible:-translate-y-0.5 md:text-base">
                      {c.index}
                    </span>
                    <span className="flex-1">
                      <span className="block text-xl font-extrabold leading-tight tracking-tight text-white md:text-2xl xl:text-3xl">
                        {entry?.label}
                      </span>
                      <span className="mt-1 block text-sm leading-snug text-white/65">
                        {entry?.context}
                      </span>
                    </span>
                    <ArrowRight
                      className="mt-1.5 h-5 w-5 shrink-0 text-white/70 transition-transform duration-300 group-hover:translate-x-1.5 group-focus-visible:translate-x-1.5"
                      aria-hidden="true"
                    />
                  </>
                );
                const handlers = {
                  onMouseEnter: () => setActive(i),
                  onMouseLeave: clear,
                  onFocus: () => setActive(i),
                  onBlur: clear,
                };
                return (
                  <li
                    key={c.key}
                    className="menu-row"
                    style={{ ["--row-delay" as string]: `${220 + i * 70}ms` }}
                  >
                    {c.hash ? (
                      <a
                        href={`/#${c.hash}`}
                        className={rowClass}
                        onClick={handleAnchor(c.hash)}
                        {...handlers}
                      >
                        {inner}
                      </a>
                    ) : (
                      <Link to={c.to as string} className={rowClass} {...handlers}>
                        {inner}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>
    </section>
  );
};

export default memo(HomeCover);
